import PhotoSwipe from "photoswipe";

/**
 * 画像を押したら拡大して見せる。
 *
 * <b>画面写真は原寸で見ないと分からない。</b>ページに置ける大きさは
 * 限られており、縮めた絵では潰れ・にじみ・切り取りの違いが分からない。
 *
 * 拡大の操作（ピンチ・ホイール・ドラッグ・キー）は PhotoSwipe に任せる。
 * <b>自前で書くと端末ごとの差を全部引き受けることになる。</b>
 */

/** 拡大して見せる 1 枚分。 */
export interface ZoomTarget {
    src: string;
    width: number;
    height: number;
    alt: string;
}

/**
 * 描画済みの画像から、拡大に必要な情報を読む。
 *
 * <b>大きさはサーバーから渡さない。</b>本体を読まずに幅と高さを知る手段がなく、
 * 一覧のために数百 KB を復号するのは割に合わない。
 * 画面に出ている画像は読み込み済みなので、そこから原寸が取れる。
 */
export function targetOf(image: HTMLImageElement): ZoomTarget | null {
    const width = image.naturalWidth;
    const height = image.naturalHeight;

    // まだ読み込めていない・壊れている。押しても何もしない
    if (width === 0 || height === 0) {
        return null;
    }

    return { src: image.currentSrc || image.src, width, height, alt: image.alt };
}

/** 実際に開く。差し替えられるようにして、検査でブラウザの実装に依存させない。 */
export type OpenZoom = (target: ZoomTarget) => void;

function openPhotoSwipe(target: ZoomTarget): void {
    new PhotoSwipe({
        dataSource: [{ src: target.src, width: target.width, height: target.height, alt: target.alt }],
        index: 0,
        // **ふわっと出す。**拡大しながら飛ばすと、差し替え前後の見比べで目が滑る
        showHideAnimationType: "fade",
        // **原寸で開く。**ただし画面に収まらないときは収まるところまで。
        // 端が見えない状態で開くと「切れている」のか「はみ出している」のか分からない
        initialZoomLevel: zoom => Math.min(1, zoom.fit),
        // 押すと必ず原寸になる（収めて開いたときの逃げ道）
        secondaryZoomLevel: 1,
        maxZoomLevel: 2,
        bgOpacity: 0.9,
        // 閉じる手段は × とキーと背景。掴んで下げる操作は誤爆しやすい
        closeOnVerticalDrag: false,
    }).init();
}

/**
 * <c>data-zoom</c> を付けた画像を押せるようにする。
 *
 * <b>ビューに <c>onclick</c> は書かない</b>（CSP に <c>'unsafe-inline'</c> を
 * 残すことになる。docs/02 §2.1）。印は属性で付けて、拾うのはここ。
 */
export function setupImageZoom(open: OpenZoom = openPhotoSwipe): void {
    for (const image of document.querySelectorAll<HTMLImageElement>("img[data-zoom]")) {
        image.classList.add("zoomable");
        image.addEventListener("click", () => {
            const target = targetOf(image);
            if (target !== null) {
                open(target);
            }
        });
    }
}
